import toast from 'react-hot-toast';

/**
 * Toast Notification Utility
 * Provides consistent toast notifications with pill-shaped design throughout the app
 */

// Custom toast styles - Pill-shaped design with project primary colors
const toastOptions = {
  duration: 4000,
  position: 'top-right',
  style: {
    background: '#FFF5E6', // primary-50
    color: '#663700', // primary-900
    padding: '12px 16px',
    borderRadius: '9999px', // Full rounded for pill shape
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    maxWidth: '420px',
    minWidth: '280px',
    border: '1px solid #FFD699', // primary-200
    fontSize: '14px',
    fontWeight: '500',
  },
  success: {
    iconTheme: {
      primary: '#10b981',
      secondary: '#fff',
    },
    style: {
      background: '#ECFDF5',
      color: '#065F46',
      border: '1px solid #A7F3D0',
    },
  },
  error: {
    iconTheme: {
      primary: '#ef4444',
      secondary: '#fff',
    },
    style: {
      background: '#FEF2F2',
      color: '#991B1B',
      border: '1px solid #FECACA',
    },
  },
};

// Success toast
export const showSuccess = (message, options = {}) => {
  return toast.success(message, {
    ...toastOptions,
    ...options,
  });
};

// Error toast
export const showError = (message, options = {}) => {
  return toast.error(message, {
    ...toastOptions,
    duration: 5000,
    ...options,
  });
};

// Info toast
export const showInfo = (message, options = {}) => {
  return toast(message, {
    ...toastOptions,
    icon: 'ℹ️',
    ...options,
  });
};

// Warning toast
export const showWarning = (message, options = {}) => {
  return toast(message, {
    ...toastOptions,
    icon: '⚠️',
    style: {
      ...toastOptions.style,
      background: '#FFF5E6', // primary-50
      color: '#663700', // primary-900
      border: '1px solid #FFD699', // primary-200
    },
    ...options,
  });
};

// Loading toast
export const showLoading = (message, options = {}) => {
  return toast.loading(message, {
    ...toastOptions,
    ...options,
  });
};

// Promise toast
export const showPromise = (promise, messages) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || 'Loading...',
      success: messages.success || 'Success!',
      error: messages.error || 'Error occurred',
    },
    toastOptions
  );
};

// Custom toast with action - Pill-shaped design
export const showCustom = (message, action, actionLabel = 'Action', options = {}) => {
  return toast.custom(
    (t) => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-primary-50 shadow-lg rounded-full pointer-events-auto flex items-center border border-primary-200 px-4 py-3`}
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center mr-3">
          <span className="text-primary-600">ℹ️</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary-900">{message}</p>
        </div>
        <button
          onClick={() => {
            action();
            toast.dismiss(t.id);
          }}
          className="flex-shrink-0 ml-3 px-4 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-xs font-semibold transition-colors"
        >
          {actionLabel}
        </button>
      </div>
    ),
    options
  );
};

// Dismiss all toasts
export const dismissAll = () => {
  toast.dismiss();
};

// Dismiss specific toast
export const dismiss = (toastId) => {
  toast.dismiss(toastId);
};

// Pre-built notification messages
export const notifications = {
  // Generic success and error
  success: (message) => showSuccess(message),
  error: (message) => showError(message),
  info: (message) => showInfo(message),
  warning: (message) => showWarning(message),

  // Session notifications
  sessionCreated: () => showSuccess('Session created successfully!'),
  sessionUpdated: () => showSuccess('Session updated successfully!'),
  sessionDeleted: () => showSuccess('Session deleted successfully!'),
  sessionStarted: () => showSuccess('Session started!'),
  sessionEnded: () => showSuccess('Session ended successfully!'),

  // Activity notifications
  activityAdded: () => showSuccess('Activity added successfully!'),
  activityUpdated: () => showSuccess('Activity updated successfully!'),
  activityDeleted: () => showSuccess('Activity deleted successfully!'),
  activityDuplicated: () => showSuccess('Activity duplicated!'),
  activitiesImported: (count) => showSuccess(`${count} activities imported successfully!`),
  activitiesExported: (format) =>
    showSuccess(`Activities exported to ${format.toUpperCase()}!`),

  // Participant notifications
  participantAdded: () => showSuccess('Participant added successfully!'),
  participantRemoved: () => showSuccess('Participant removed!'),
  participantsInvited: (count) => showSuccess(`${count} participants invited!`),

  // File notifications
  fileUploaded: (filename) => showSuccess(`${filename} uploaded successfully!`),
  fileDeleted: (filename) => showSuccess(`${filename} deleted!`),

  // Response notifications
  responseSaved: () => showSuccess('Response saved!'),
  responseSubmitted: () => showSuccess('Response submitted successfully!'),

  // Error notifications
  networkError: () => showError('Network error. Please check your connection.'),
  permissionDenied: () => showError('Permission denied!'),
  notFound: () => showError('Resource not found!'),
  validationError: (message) => showError(message || 'Validation error occurred!'),
  serverError: () => showError('Server error. Please try again later.'),

  // Warning notifications
  unsavedChanges: () => showWarning('You have unsaved changes!'),
  sessionEnding: (minutes) => showWarning(`Session ending in ${minutes} minutes!`),
  lowBattery: () => showWarning('Device battery is low!'),

  // Info notifications
  copied: () => showInfo('Copied to clipboard!'),
  saved: () => showInfo('Saved!'),
  loading: () => showLoading('Loading...'),
};
